<template>
  <v-row>
    <v-col cols="12" md="4">
      <v-card>
        <v-card-title class="d-flex align-center">
          <span>Szállás igény részletei</span>
          <v-spacer />
          <v-tooltip v-if="!canEdit" location="bottom">
            <template #activator="{ props: tooltipProps }">
              <span v-bind="tooltipProps">
                <v-btn
                  color="primary"
                  variant="text"
                  size="small"
                  prepend-icon="mdi-pencil"
                  :disabled="true"
                >
                  Módosítás
                </v-btn>
              </span>
            </template>
            <span>{{ editBlockedReason }}</span>
          </v-tooltip>
          <v-btn
            v-else
            color="primary"
            variant="text"
            size="small"
            prepend-icon="mdi-pencil"
            @click="$emit('editInquiry')"
          >
            Módosítás
          </v-btn>
        </v-card-title>
        <v-container>
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                :model-value="
                  formatPublicReference(
                    props.accommodationInquiry.publicReference
                  )
                "
                label="Referencia"
                variant="outlined"
                readonly
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                :model-value="props.accommodationInquiry.costCenterName"
                label="Költséghely"
                variant="outlined"
                readonly
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                :model-value="props.accommodationInquiry.userName"
                label="Felhasználó"
                variant="outlined"
                readonly
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col sm="4">
              <v-text-field
                v-model="props.accommodationInquiry.area"
                label="Helység"
                variant="outlined"
                readonly
              ></v-text-field>
            </v-col>
            <v-col sm="4">
              <v-text-field
                v-model="props.accommodationInquiry.numberOfPeople"
                label="Létszám"
                variant="outlined"
                readonly
              ></v-text-field>
            </v-col>
            <v-col sm="4">
              <v-text-field
                :model-value="
                  inquiryStatusLabel(props.accommodationInquiry.status)
                "
                label="Státusz"
                variant="outlined"
                readonly
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="props.accommodationInquiry.from"
                label="Érkezés"
                variant="outlined"
                readonly
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="props.accommodationInquiry.to"
                label="Távozás"
                variant="outlined"
                readonly
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field
                :model-value="
                  placementTypeLabel(props.accommodationInquiry.placementType)
                "
                label="Elhelyezés"
                variant="outlined"
                readonly
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="props.accommodationInquiry.contactName"
                label="Kapcsolattartó neve"
                variant="outlined"
                readonly
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="props.accommodationInquiry.contactPhone"
                label="Kapcsolattartó telefonszáma"
                variant="outlined"
                readonly
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-textarea
                v-model="props.accommodationInquiry.catering"
                label="Ellátás"
                variant="outlined"
                rows="2"
                readonly
                hide-details
              ></v-textarea>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-textarea
                v-model="props.accommodationInquiry.comment"
                label="Komment"
                variant="outlined"
                rows="2"
                readonly
                hide-details
              ></v-textarea>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-col>
    <v-spacer></v-spacer>
    <v-col cols="12" md="8">
      <v-card class="offers-card">
        <v-card-title> Ajánlatok </v-card-title>
        <v-container
          v-if="props.accommodationInquiry.offerEntityDTOS.length > 0"
        >
          <div
            v-for="offer in props.accommodationInquiry.offerEntityDTOS"
            :key="offer.id"
          >
            <CustomerOfferTable
              :offer="offer"
              :can-accept="!hasAcceptedOffer"
              @acceptOffer="acceptOffer"
              @declineOffer="declineOffer"
            ></CustomerOfferTable>
          </div>
        </v-container>
        <v-container v-else> Még nincsenek ajánlatok </v-container>
        <v-card-actions class="pa-4">
          <!-- Primary action -->
          <v-spacer></v-spacer>
          <v-btn
            v-if="hasBooking"
            color="primary"
            :to="`/bookings/${props.accommodationInquiry.publicReference}`"
          >
            Tovább a foglaláshoz
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-col>
  </v-row>
</template>
<script lang="ts">
import { computed, defineComponent, PropType } from "vue";
import router from "@/router";
import { OfferService } from "@/services/OfferService";
import CustomerOfferTable from "@/components/AccommodationInquiries/customer/offer/CustomerOfferTable.vue";
import { useToast } from "@/services/useToast";
import {
  inquiryStatusLabel,
  placementTypeLabel,
  isTerminalInquiryStatus,
} from "@/utils/statusLabels";

export default defineComponent({
  components: { CustomerOfferTable },
  props: {
    accommodationInquiry: {
      type: Object as PropType<any>,
      required: true,
    },
  },
  emits: ["offerAccepted", "offerDeclined", "editInquiry"],
  setup(props, ctx) {
    const offerService = new OfferService();
    const inquiryId: string = router.currentRoute.value.params.id as string;
    const hasBooking = computed(
      () => props.accommodationInquiry.hasBooking == true
    );
    const hasAcceptedOffer = computed(() =>
      props.accommodationInquiry.offerEntityDTOS?.some(
        (offer: any) => offer.status === "ACCEPTED"
      )
    );
    /**
     * Only hides the button -- the server decides. It answers the same two questions: no
     * accepted offer, and the inquiry not closed by hand.
     */
    const canEdit = computed(
      () =>
        !hasAcceptedOffer.value &&
        !isTerminalInquiryStatus(props.accommodationInquiry.status)
    );
    /** Why the button is greyed out. A disabled control that cannot say why is a dead end. */
    const editBlockedReason = computed(() =>
      hasAcceptedOffer.value
        ? "Elfogadott ajánlat után az igény már nem módosítható"
        : "A lezárt igény már nem módosítható"
    );
    const { showToast } = useToast();

    const acceptOffer = async (offerId: number) => {
      try {
        await offerService.acceptOffer(offerId);
        ctx.emit("offerAccepted", offerId);
        showToast("Ajánlat elfogadva");
      } catch (error) {
        console.error(error);
      }
    };

    /**
     * Reloads either way: on success to pick up the new status, and after a rejection because
     * the screen is then stale -- the offer was answered by somebody else, or an edit
     * invalidated it, and only a reload says which.
     */
    const declineOffer = async (offerId: number) => {
      try {
        await offerService.declineOffer(offerId);
        showToast("Ajánlat elutasítva");
      } catch (error) {
        console.error(error);
        showToast("Az ajánlatot nem sikerült elutasítani");
      } finally {
        ctx.emit("offerDeclined", offerId);
      }
    };
    function formatPublicReference(ref: string) {
      if (!ref) return "";
      return ref.match(/.{1,4}/g)!.join("-");
    }
    return {
      props,
      inquiryId,
      hasAcceptedOffer,
      canEdit,
      editBlockedReason,
      acceptOffer,
      declineOffer,
      formatPublicReference,
      hasBooking,
      inquiryStatusLabel,
      placementTypeLabel,
    };
  },
});
</script>
<style scoped></style>
